import { PressableProps } from '@unistocks/components/basic/pressable';
import { Button } from '@unistocks/components/button/button';
import { Input } from '@unistocks/components/input/input';
import { Row } from '@unistocks/components/layout/flex';
import { ModalHeader } from '@unistocks/components/modal/modal-header';
import { Paragraph } from '@unistocks/components/text/text';
import { t } from '@unistocks/localization';
import { breakpoints } from '@unistocks/shared/breakpoints';
import { useBackHandler } from '@unistocks/shared/hooks/use-backhandler';
import { noop } from '@unistocks/shared/utils/util';
import React, { memo, useCallback, useState } from 'react';
import { AlertDialogProps, Dialog, useControllableState } from 'tamagui';
import { useModalContext } from '../../providers/modal-context';

export enum ConfirmType {
  Info = 'info',
  Warning = 'warning',
  Error = 'error'
}

export type ConfirmProps = AlertDialogProps & {
  /**
   * Whether the confirm dialog is enabled.
   *
   * If true, the dialog will be shown when the trigger is pressed.
   *
   * If false, the trigger will be executed directly.
   * @default true
   */
  enabled?: boolean;
  /**
   * Not implemented yet
   */
  type?: ConfirmType;
  title?: string;
  message?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  cancelText?: string;
  confirmText?: string;
  requiredInputConfirmation?: string;
  requiredInputConfirmationMessage?: string;
  isLoading?: boolean;
};

export const Confirm = memo<ConfirmProps>(
  ({
    enabled = true,
    type = ConfirmType.Info,
    title = t('AbpUi::Confirm'),
    message = t('AbpUi::AreYouSure'),
    onConfirm,
    onCancel,
    cancelText = t('::Cancel'),
    confirmText = t('::Ok'),
    requiredInputConfirmation,
    requiredInputConfirmationMessage = t('::Confirm:PleaseEnterTheFollowing'),
    isLoading,
    children,
    ...props
  }) => {
    const [confirmInput, setConfirmInput] = useState('');
    const disabled =
      isLoading || (!!requiredInputConfirmation && confirmInput !== requiredInputConfirmation);

    const [open, onOpenChange] = useControllableState({
      strategy: 'prop-wins',
      defaultProp: false,
      prop: props.open,
      onChange: props.onOpenChange
    });

    useModalContext(open ?? false);
    useBackHandler(useCallback(() => open, [open]));

    if (!enabled)
      return React.cloneElement<PressableProps>(children as any, { onPress: onConfirm });

    return (
      <Dialog {...props} modal open={open} onOpenChange={onOpenChange}>
        <Dialog.Trigger asChild>{children}</Dialog.Trigger>

        <Dialog.Portal zIndex={10_000_000}>
          <Dialog.Overlay
            transition={'quickLessBouncy'}
            animateOnly={['opacity']}
            bg={'$shadow6'}
            enterStyle={{ o: 0 }}
            exitStyle={{ o: 0 }}
            onPress={noop}
          />

          <Dialog.Content
            testID={'confirm-content'}
            trapFocus
            bordered
            elevate
            transition={'quickLessBouncy'}
            animateOnly={['transform', 'opacity']}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            w={'95%'}
            maxWidth={breakpoints.xs}
            $sm={{ w: 'auto', minWidth: breakpoints.xxs }}
            p={'$3'}
            mx={'$3'}
            onPointerDownOutside={e => e.preventDefault()}
          >
            <ModalHeader title={title} closeButton={false} />

            <Dialog.Description my={'$2'} size={'$5'}>
              {message}
            </Dialog.Description>

            {requiredInputConfirmation ? (
              <>
                <Paragraph>{requiredInputConfirmationMessage}</Paragraph>

                <Input
                  size={'$4'}
                  contentContainerProps={{ my: '$3' }}
                  placeholder={requiredInputConfirmation}
                  autoFocus
                  onChangeText={setConfirmInput}
                />
              </>
            ) : null}

            <Row mt={'$2'} justifyContent="flex-end">
              <Dialog.Close asChild onPress={onCancel} testID="cancel-button">
                <Button variant={'outlined'} size={'$3'} $max-sm={{ flex: 1 }}>
                  {cancelText}
                </Button>
              </Dialog.Close>

              <Dialog.Close asChild onPress={onConfirm} disabled={disabled} testID="confirm-button">
                <Button size={'$3'} loading={isLoading} $max-sm={{ flex: 1 }}>
                  {confirmText}
                </Button>
              </Dialog.Close>
            </Row>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    );
  }
);

Confirm.displayName = 'Confirm';
